import { useRef, useState } from 'react'
import { Box } from '@mui/material'
import clsx from 'clsx'

import TextField from '../TextField'
import Typography, { type TypographyProps } from '../Typography'

import './EditableName.scss'

export interface EditableNameProps {
  value: string
  onChange: (value: string) => void
  variant?: TypographyProps['variant']
  readonly?: boolean
  className?: string
}

/**
 * Click the name to edit it inline. Typing reports the value to the parent immediately,
 * Enter or clicking away keeps it trimmed, Escape restores the value the edit started from.
 * The value is only reported to the parent, never persisted here.
 */
const EditableName: React.FC<EditableNameProps> = ({
  value,
  onChange,
  variant = 'h6',
  readonly = false,
  className,
}) => {
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState(value)
  const valueAtEditStart = useRef(value)
  const resolvedRef = useRef(false)

  const confirmEdit = (): void => {
    const trimmed = editValue.trim()
    onChange(trimmed || valueAtEditStart.current)
    setEditing(false)
  }

  const cancelEdit = (): void => {
    resolvedRef.current = true
    onChange(valueAtEditStart.current)
    setEditing(false)
  }

  if (readonly) {
    return (
      <Typography variant={variant} className={className}>
        {value}
      </Typography>
    )
  }

  if (editing) {
    return (
      <Box className={clsx('editable-name', className)}>
        <TextField
          value={editValue}
          onChange={(e) => {
            setEditValue(e.target.value)
            onChange(e.target.value)
          }}
          autoFocus
          className='editable-name__field'
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              resolvedRef.current = true
              confirmEdit()
            }
            if (e.key === 'Escape') {
              cancelEdit()
            }
          }}
          onBlur={() => {
            if (!resolvedRef.current) confirmEdit()
            resolvedRef.current = false
          }}
        />
      </Box>
    )
  }

  return (
    <Box className={clsx('editable-name', className)}>
      <Typography
        variant={variant}
        className='editable-name__text'
        onClick={() => {
          setEditValue(value)
          valueAtEditStart.current = value
          resolvedRef.current = false
          setEditing(true)
        }}
      >
        {value}
      </Typography>
    </Box>
  )
}

export default EditableName
